import * as XLSX from 'xlsx';

const MONTHS:any = {
  'JANVIER': 1, 'FÉVRIER': 2, 'FEVRIER': 2, 'MARS': 3, 'AVRIL': 4,
  'MAI': 5, 'JUIN': 6, 'JUILLET': 7, 'AOÛT': 8, 'AOUT': 8,
  'SEPTEMBRE': 9, 'OCTOBRE': 10, 'NOVEMBRE': 11, 'DÉCEMBRE': 12, 'DECEMBRE': 12,
};

const norm = (v:any) => String(v ?? '').trim().toUpperCase();
const num = (v:any) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).replace(/\s/g,'').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const period = (fiscalYear:string, monthNo:number) => `${fiscalYear}${String(monthNo).padStart(2,'0')}`;

export const workbookToSheets = (workbook:XLSX.WorkBook) => {
  return workbook.SheetNames.map((sheetname) => {
    const ws = workbook.Sheets[sheetname];
    const rows:any[][] = XLSX.utils.sheet_to_json(ws, { header:1, defval:null, raw:true });
    const colcount = rows.reduce((m,r)=>Math.max(m,r.length),0);
    return { sheetname, rowcount:rows.length, colcount, rows };
  });
};

export const parseDeterminantForecastMetrics = (workbook:XLSX.WorkBook, fiscalYear:string) => {
  const metrics:any[] = [];

  const add = (row:any) => metrics.push({
    pdc: row.pdc || '',
    mno: row.mno || null,
    mtc: row.mtc,
    enc: row.enc || 'ALL',
    lcc: row.lcc || 'ALL',
    qty: row.qty ?? null,
    upr: row.upr ?? null,
    amt: row.amt ?? null,
    rte: row.rte ?? null,
    unc: row.unc || '',
    ssh: row.ssh,
    srf: row.srf || '',
    nts: row.nts || '',
    exi: row.exi || {},
  });

  // DENSITIES
  if (workbook.Sheets['DENSITES']) {
    const rows:any[][] = XLSX.utils.sheet_to_json(workbook.Sheets['DENSITES'], { header:1, defval:null, raw:true });
    rows.forEach((r, idx) => {
      const product = norm(r[0]);
      const density = num(r[1]);
      if (product && density !== null && product !== 'PRODUITS') {
        add({ pdc:fiscalYear, mtc:'DENSITY', enc:product.replace('GAZ','GPL'), rte:density, unc:'DENSITY', ssh:'DENSITES', srf:`R${idx+1}` });
      }
    });
  }

  // MONTHLY COEFFICIENTS
  if (workbook.Sheets['COEFFICIENTS MENSUELS']) {
    const rows:any[][] = XLSX.utils.sheet_to_json(workbook.Sheets['COEFFICIENTS MENSUELS'], { header:1, defval:null, raw:true });
    const header = rows[3] || [];
    for (let i=4; i<rows.length; i++) {
      const monthName = norm(rows[i][0]);
      const m = MONTHS[monthName];
      if (!m) continue;
      for (let c=1; c<header.length; c++) {
        const enc = norm(header[c]).replace('SUPER 91','SUPER').replace('GAZ (TM)','GPL').replace('GAZ','GPL').replace('JET A1','JETA1');
        const coef = num(rows[i][c]);
        if (enc && coef !== null) {
          add({ pdc:period(fiscalYear,m), mno:m, mtc:'MONTH_COEF', enc, rte:coef, unc:'COEF', ssh:'COEFFICIENTS MENSUELS', srf:`R${i+1}C${c+1}` });
        }
      }
    }
  }

  // Forecast monthly sales volume
  if (workbook.Sheets['PREV VTES PAR MOIS VOLUME 2025']) {
    const rows:any[][] = XLSX.utils.sheet_to_json(workbook.Sheets['PREV VTES PAR MOIS VOLUME 2025'], { header:1, defval:null, raw:true });
    const header = rows[3] || [];
    for (let i=4; i<rows.length; i++) {
      const monthName = norm(rows[i][0]);
      const m = MONTHS[monthName];
      if (!m) continue;
      for (let c=1; c<header.length; c++) {
        const enc = norm(header[c]).replace('SUPER 91','SUPER').replace('GAZ ','GPL').replace('GAZ','GPL').replace('JET A1','JETA1');
        if (!enc || enc === 'TOTAL') continue;
        const qty = num(rows[i][c]);
        if (qty !== null) {
          add({ pdc:period(fiscalYear,m), mno:m, mtc:'SALE_VOLUME', enc, lcc:'ALL', qty, unc:'M3', ssh:'PREV VTES PAR MOIS VOLUME 2025', srf:`R${i+1}C${c+1}` });
        }
      }
    }
  }

  // Forecast monthly purchase volume
  if (workbook.Sheets['PREVISIONS ACHATS PAR MOIS VOL']) {
    const rows:any[][] = XLSX.utils.sheet_to_json(workbook.Sheets['PREVISIONS ACHATS PAR MOIS VOL'], { header:1, defval:null, raw:true });
    const header = rows[3] || [];
    for (let i=4; i<rows.length; i++) {
      const monthName = norm(rows[i][0]);
      const m = MONTHS[monthName];
      if (!m) continue;
      for (let c=1; c<header.length; c++) {
        const enc = norm(header[c]).replace('SUPER 91','SUPER').replace('GAZ ','GPL').replace('GAZ','GPL').replace('JET A1','JETA1');
        if (!enc || enc === 'TOTAL') continue;
        const qty = num(rows[i][c]);
        if (qty !== null) {
          add({ pdc:period(fiscalYear,m), mno:m, mtc:'PURCHASE_VOLUME', enc, lcc:'ALL', qty, unc:'TM', ssh:'PREVISIONS ACHATS PAR MOIS VOL', srf:`R${i+1}C${c+1}` });
        }
      }
    }
  }

  // Forecast monthly purchase FCFA
  if (workbook.Sheets['PREVISIONS ACHATS PAR MOIS FCFA']) {
    const rows:any[][] = XLSX.utils.sheet_to_json(workbook.Sheets['PREVISIONS ACHATS PAR MOIS FCFA'], { header:1, defval:null, raw:true });
    const header = rows[3] || [];
    for (let i=4; i<rows.length; i++) {
      const monthName = norm(rows[i][0]);
      const m = MONTHS[monthName];
      if (!m) continue;
      for (let c=1; c<header.length; c++) {
        const enc = norm(header[c]).replace('SUPER 91','SUPER').replace('GAZ ','GPL').replace('GAZ','GPL').replace('JET A1','JETA1');
        if (!enc || enc === 'TOTAL') continue;
        const amt = num(rows[i][c]);
        if (amt !== null) {
          add({ pdc:period(fiscalYear,m), mno:m, mtc:'PURCHASE_AMOUNT', enc, lcc:'ALL', amt, unc:'FCFA', ssh:'PREVISIONS ACHATS PAR MOIS FCFA', srf:`R${i+1}C${c+1}` });
        }
      }
    }
  }

  // Resultat previsionnel partial result lines
  if (workbook.Sheets['RESULTAT PREVISIONNEL']) {
    const rows:any[][] = XLSX.utils.sheet_to_json(workbook.Sheets['RESULTAT PREVISIONNEL'], { header:1, defval:null, raw:true });
    rows.forEach((r, idx) => {
      const label = norm(r[0]);
      const amt = num(r[1]);
      if (!label || amt === null) return;

      if (label.includes("VENTES D'HYDROCARBURES")) add({ pdc:fiscalYear, mtc:'SALE_AMOUNT', enc:'ALL', lcc:'ALL', amt, unc:'FCFA', ssh:'RESULTAT PREVISIONNEL', srf:`R${idx+1}`, nts:r[0] });
      if (label.includes('ACHATS')) add({ pdc:fiscalYear, mtc:'PURCHASE_AMOUNT', enc:'ALL', lcc:'ALL', amt, unc:'FCFA', ssh:'RESULTAT PREVISIONNEL', srf:`R${idx+1}`, nts:r[0] });
      if (label.includes('RESULTAT') || label.includes('PARTIEL')) add({ pdc:fiscalYear, mtc:'PARTIAL_RESULT', enc:'ALL', lcc:'ALL', amt, unc:'FCFA', ssh:'RESULTAT PREVISIONNEL', srf:`R${idx+1}`, nts:r[0] });
    });
  }

  return metrics;
};

export const readWorkbookFile = async (file:File) => {
  const data = await file.arrayBuffer();
  return XLSX.read(data, { type:'array', cellDates:true });
};
